import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, LogOut, Heart, CalendarClock, CheckCircle2, Bell, ListChecks, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { MatchRevealModal } from "../components/MatchRevealModal";
import { DashboardStatCards } from "../components/DashboardStatCards";
import { DashboardActionItems } from "../components/DashboardActionItems";
import { ProfilePhotoUpload } from "../components/ProfilePhotoUpload";
import { ReportConcernModal, REPORT_TYPES } from "../components/ReportConcernModal";
import { formatDate } from "../lib/dateUtils";

const REPORT_TYPE_LABELS = Object.fromEntries(REPORT_TYPES.map((t) => [t.value, t.label]));

const STATUS_LABEL = {
  registered: { label: "Waiting to be matched", color: "bg-soft-gray text-midnight-black" },
  waitlisted: { label: "On the waitlist", color: "bg-amber-100 text-amber-700" },
  matched: { label: "You've been matched", color: "bg-stranger-orange text-clean-white" },
  completed: { label: "Story ready", color: "bg-midnight-black text-clean-white" },
  cancelled: { label: "Cancelled", color: "bg-soft-gray text-midnight-black/60" },
};

export default function StrangerDashboard() {
  const { user, setUser, logout } = useAuth();
  const [bookings, setBookings] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [revealBooking, setRevealBooking] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    api.get("/bookings/mine").then(({ data }) => {
      setBookings(data);
      const unseenMatch = data.find((b) => b.status === "matched" && !b.match_seen_by_stranger);
      if (unseenMatch) setRevealBooking(unseenMatch);
    });
    api
      .get("/photographers/favorites/mine")
      .then(({ data }) => setFavorites(data))
      .catch(() => {});
    api
      .get("/bookings/dashboard-summary")
      .then(({ data }) => setSummary(data))
      .catch(() => {});
    api
      .get("/safety/reports/mine")
      .then(({ data }) => setReports(data))
      .catch(() => {});
  }, []);

  const reviewedUnseenReports = reports.filter((r) => ["resolved", "closed"].includes(r.status) && !r.report_seen_by_stranger);

  const dismissReportNotice = (reportId) => {
    api.put(`/safety/reports/${reportId}/seen`).then(() => {
      setReports((prev) => prev.map((r) => (r.id === reportId ? { ...r, report_seen_by_stranger: true } : r)));
    });
  };

  const statCards = summary
    ? [
        { key: "upcoming_sessions", label: "Upcoming", value: summary.stats.upcoming_sessions, icon: CalendarClock },
        { key: "completed_sessions", label: "Completed", value: summary.stats.completed_sessions, icon: CheckCircle2 },
        { key: "unread_notifications", label: "Unread", value: summary.stats.unread_notifications, icon: Bell },
        { key: "action_items", label: "Needs Attention", value: summary.stats.action_items, icon: ListChecks },
      ]
    : [];

  return (
    <>
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14" data-testid="stranger-dashboard-page">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="text-stranger-orange text-xs font-bold tracking-[0.25em] uppercase mb-2">Your Dashboard</p>
          <h1 className="font-extrabold uppercase text-2xl sm:text-3xl tracking-tight">Hey {user.first_name}</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowReportModal(true)}
            data-testid="dashboard-report-safety-button"
            className="relative flex items-center gap-2 text-xs font-bold tracking-widest px-5 py-3 rounded-full border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-clean-white transition-colors"
          >
            <ShieldAlert className="w-3.5 h-3.5" /> REPORT SAFETY CONCERN
            {reviewedUnseenReports.length > 0 && (
              <span data-testid="report-safety-badge" className="absolute -top-2 -right-2 bg-red-600 text-clean-white text-[10px] font-extrabold w-5 h-5 rounded-full flex items-center justify-center">
                {reviewedUnseenReports.length}
              </span>
            )}
          </button>
          <Link to="/register" data-testid="edit-stranger-profile-link">
            <button className="text-xs font-bold tracking-widest border-2 border-midnight-black px-5 py-3 rounded-full hover:bg-midnight-black hover:text-clean-white transition-colors">EDIT PROFILE</button>
          </Link>
          <button onClick={logout} data-testid="logout-button" className="flex items-center gap-2 text-xs font-bold tracking-widest px-5 py-3 rounded-full border-2 border-soft-gray hover:border-stranger-orange hover:text-stranger-orange transition-colors">
            <LogOut className="w-3.5 h-3.5" /> LOG OUT
          </button>
        </div>
      </div>

      {summary && <DashboardStatCards cards={statCards} />}

      {user?.photo_status === "rejected" && (
        <div className="border-2 border-red-200 bg-red-50 rounded-2xl p-6 mb-14" data-testid="photo-rejected-banner">
          <h2 className="font-extrabold uppercase text-sm mb-4 text-red-600">Your profile photo needs an update</h2>
          <ProfilePhotoUpload user={user} onUploaded={(data) => setUser((u) => ({ ...u, ...data }))} />
        </div>
      )}

      {summary && (
        <div className="mb-14">
          <h2 className="font-extrabold uppercase text-lg mb-4">Needs Your Attention</h2>
          <DashboardActionItems items={summary.action_items} emptyMessage="All caught up — nothing needs your attention right now." />
        </div>
      )}

      {reviewedUnseenReports.length > 0 && (
        <div className="mb-14" data-testid="report-update-card">
          <h2 className="font-extrabold uppercase text-lg mb-4">Report Update</h2>
          <div className="space-y-3">
            {reviewedUnseenReports.map((r) => (
              <div key={r.id} data-testid={`report-update-row-${r.id}`} className="border-2 border-red-200 bg-red-50 rounded-2xl p-5 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-extrabold uppercase text-xs text-red-600 mb-1">
                    Your {REPORT_TYPE_LABELS[r.report_type] || r.report_type} report has been {r.status === "closed" ? "closed" : "reviewed"}
                  </p>
                  {r.resolution && <p className="text-sm font-medium text-midnight-black/70">{r.resolution}</p>}
                </div>
                <button
                  data-testid={`dismiss-report-update-${r.id}`}
                  onClick={() => dismissReportNotice(r.id)}
                  className="text-xs font-bold tracking-widest border-2 border-red-600 text-red-600 px-4 py-2 rounded-full hover:bg-red-600 hover:text-clean-white transition-colors shrink-0"
                >
                  DISMISS
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h2 className="font-extrabold uppercase text-lg">Your Sessions</h2>
        <Link to="/find-a-session" data-testid="find-another-session-link" className="text-stranger-orange font-bold text-sm hover:underline">
          FIND ANOTHER SESSION →
        </Link>
      </div>

      {bookings === null ? (
        <p className="font-medium text-midnight-black/60" data-testid="bookings-loading">
          Loading your sessions...
        </p>
      ) : bookings.length === 0 ? (
        <div className="border border-soft-gray rounded-2xl p-10 text-center" data-testid="no-bookings">
          <p className="font-medium text-midnight-black/70 mb-4">You haven't booked a Stranger Session yet.</p>
          <Link to="/find-a-session">
            <button className="bg-stranger-orange text-clean-white font-bold tracking-widest text-xs px-6 py-3 rounded-full hover:bg-orange-600 transition-colors">FIND A SESSION</button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {bookings.map((b) => (
            <Link key={b.id} to={`/dashboard/bookings/${b.id}`} data-testid={`booking-card-${b.id}`} className="border border-soft-gray rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300">
              <div className="relative h-40">
                <img src={b.session?.image_url} alt={b.session?.title} className="w-full h-full object-cover" />
                <span className={`absolute top-3 left-3 text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full ${STATUS_LABEL[b.status]?.color}`}>{STATUS_LABEL[b.status]?.label}</span>
              </div>
              <div className="p-5">
                <h3 className="font-extrabold uppercase text-sm mb-2">{b.session?.title}</h3>
                <p className="text-xs font-medium text-midnight-black/60 flex items-center gap-1.5 mb-1">
                  <MapPin className="w-3.5 h-3.5 text-stranger-orange" /> {b.session?.city}, {b.session?.state}
                </p>
                <p className="text-xs font-medium text-midnight-black/60 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-stranger-orange" /> {formatDate(b.session?.session_date)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {favorites.length > 0 && (
        <div className="mt-14">
          <h2 className="font-extrabold uppercase text-lg mb-6 flex items-center gap-2">
            <Heart className="w-4 h-4 text-stranger-orange" /> Favorite Photographers
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {favorites.map((f) => (
              <Link key={f.id} to={`/photographers/${f.slug || f.id}`} data-testid={`favorite-photographer-${f.id}`} className="text-center">
                <img src={f.profile_photo_url} alt={f.business_name} className="w-16 h-16 rounded-full object-cover mx-auto mb-2 border-2 border-stranger-orange" />
                <p className="text-xs font-bold flex items-center justify-center gap-1">
                  {f.business_name} {f.verified && <VerifiedBadge compact />}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>

      {revealBooking && <MatchRevealModal booking={revealBooking} onDismiss={() => setRevealBooking(null)} />}
      {showReportModal && <ReportConcernModal bookings={bookings || []} onClose={() => setShowReportModal(false)} />}
    </>
  );
}
